import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { AppUseDispatch, AppUseSelector } from '../../redux/hooks'
import { SET_ONBOARDING_GROUP, SET_GROUP_REVEAL } from '../../redux/types'
import store from '../../redux/store'
import { trackOnboarding } from '../../utils/analytics'
import {
  FLOW_GROUPS,
  FlowContext,
  GroupKey,
  POST_GATE_GROUPS,
  StepKey,
  findGroup,
  firstGroup,
  groupProgress,
  groupSteps,
  nextGroupKey,
  prevGroupKey,
} from './flowConfig'
import { useOnboardingBootstrap } from './useOnboardingBootstrap'
import { capturePartnerCode } from '../../utils/offerCoupon'
import NameStep from './steps/NameStep'
import GoalStep from './steps/GoalStep'
import ExperienceStep from './steps/ExperienceStep'
import ExamStep from './steps/ExamStep'
import WritingStep from './steps/WritingStep'
import PlacementStep from './steps/PlacementStep'
import GateStep from './steps/GateStep'
import ResultStep from './steps/ResultStep'
import AttributionStep from './steps/AttributionStep'
import TrialOffer from './TrialOffer'
import './onboarding.css'

const STEP_COMPONENTS: Record<StepKey, React.ComponentType<{ next: () => void }>> = {
  name: NameStep,
  goal: GoalStep,
  experience: ExperienceStep,
  exam: ExamStep,
  writing: WritingStep,
  placement: PlacementStep,
  gate: GateStep,
  result: ResultStep,
  attribution: AttributionStep,
  trial: TrialOffer,
}

// The brand name, not copy: it stays the same in every language.
const BRAND_NAME = 'maayot'

/**
 * Onboarding orchestrator.
 *
 * Several questions share a screen under one header. Only the first is visible
 * to begin with; answering it reveals the next beneath it, and once the last one
 * is answered a single Continue moves on to the next group. Answered questions
 * stay on screen and stay editable, so a misclick is fixed in place rather than
 * by walking backwards through the flow.
 *
 * The flow is declared in flowConfig.ts; this component resolves the current
 * group, owns the auth redirects, and renders the shared chrome (logo, back
 * button, labeled progress bar).
 */
const OnBoarding = () => {
  const { t } = useTranslation('onboarding')
  const dispatch = AppUseDispatch()
  const { group, groupReveal, experience, languageInfo, placementDone } = AppUseSelector(
    ({ onboarding }) => onboarding,
  )
  const startedTrackedRef = useRef(false)
  const lastRevealedRef = useRef<HTMLDivElement | null>(null)

  // A partner link (/signup?code=MBM102026) carries its code to the plans step, where it
  // is checked against Stripe before any discount is shown.
  //
  // Captured during this render rather than in an effect, because the plans step reads the
  // stored code while *it* renders — and children render before a parent's effects run.
  // `group` is persisted, so a learner who already reached the plans step and later clicks
  // the partner link mounts both in one commit: an effect here would write the code just
  // after the step had already looked for it and found nothing, losing the discount for
  // exactly the visit the link was meant to serve.
  useState(() => capturePartnerCode(window.location.search))

  // `?group=customize` (or level/result/trial) opens the flow on that screen for
  // review, so a reviewer does not have to sign up again to reach a later one.
  // Applied once per mount so it does not fight the user's own navigation.
  const jumpRef = useRef(false)
  useEffect(() => {
    if (jumpRef.current) return
    jumpRef.current = true
    const wanted = new URLSearchParams(window.location.search).get('group')
    if (wanted && FLOW_GROUPS.some((g) => g.key === wanted)) {
      dispatch({ type: SET_ONBOARDING_GROUP, payload: wanted })
    }
  }, [])

  // Read these directly rather than from the bootstrap hook: the hook needs to
  // be told whether the user is past the gate, and working that out needs them.
  const { pathname } = useLocation()
  const isExistingFlow = pathname.includes('/existing')
  const authToken = localStorage.getItem('authToken')

  const currentGroupKey = (group as GroupKey) || null

  const ctx: FlowContext = {
    experience,
    hasWritingPreferences: Boolean(languageInfo?.preferences?.length),
    isAuthed: Boolean(authToken),
    isExistingFlow,
  }

  const activeGroupKey: GroupKey = currentGroupKey || firstGroup(ctx)
  const activeGroup = findGroup(activeGroupKey)

  // Verifying the emailed code authenticates the user *in the middle* of the
  // identity group, which would make the name and email questions inapplicable
  // and yank them off the screen the user is still looking at. So the group's
  // contents are resolved against the auth state as it was when the group
  // opened. Everything else in the context stays live, because a question can
  // legitimately unlock a later one in the same group — answering "yes, I've
  // studied before" is what brings up the script question.
  const entryAuthRef = useRef<{ key: GroupKey; wasAuthed: boolean } | null>(null)
  if (!entryAuthRef.current || entryAuthRef.current.key !== activeGroupKey) {
    entryAuthRef.current = { key: activeGroupKey, wasAuthed: Boolean(authToken) }
  }
  const groupCtx: FlowContext = { ...ctx, isAuthed: entryAuthRef.current.wasAuthed }
  const steps = activeGroup ? groupSteps(activeGroup, groupCtx) : []

  // Signing in flips credentials.customFields.user_exist, which the bootstrap
  // reads as "returning user on the signup page" and answers by redirecting to
  // /stories. The gate is one question among three and verification only
  // reveals the next one, leaving the user on a group the redirect does not
  // consider protected — so signing up threw them out of their own signup.
  // Authenticating *during* the identity group therefore counts as past the
  // gate; arriving already authenticated still redirects as before.
  const authedThisGroup = activeGroupKey === 'identity' && Boolean(authToken) && !entryAuthRef.current.wasAuthed
  const isPostGate = POST_GATE_GROUPS.includes(activeGroupKey) || authedThisGroup
  useOnboardingBootstrap(isPostGate)

  // Steps dispatch their answer right before calling next(), so the context
  // must be read fresh from the store at call time — the render-scope ctx is
  // one update behind (stale closure).
  const liveCtx = (): FlowContext => {
    const onboarding = (store.getState() as any).onboarding
    return {
      experience: onboarding?.experience || '',
      hasWritingPreferences: Boolean(onboarding?.languageInfo?.preferences?.length),
      isAuthed: Boolean(localStorage.getItem('authToken')),
      isExistingFlow,
    }
  }

  // --- analytics ---
  useEffect(() => {
    if (!startedTrackedRef.current) {
      startedTrackedRef.current = true
      if (!group) {
        trackOnboarding('onboarding_started', { variant: isExistingFlow ? 'existing' : 'default' })
      }
    }
    trackOnboarding('onboarding_group_viewed', { group: activeGroupKey })
  }, [activeGroupKey])

  // A group can empty out under the user — auth landing removes the name and
  // email questions from the identity group — so move on rather than render a
  // headed screen with nothing in it.
  useEffect(() => {
    if (steps.length > 0) return
    const nextKey = nextGroupKey(activeGroupKey, ctx)
    if (nextKey) dispatch({ type: SET_ONBOARDING_GROUP, payload: nextKey })
  }, [activeGroupKey, steps.length])

  const goToGroup = (key: GroupKey) => {
    dispatch({ type: SET_ONBOARDING_GROUP, payload: key })
    window.scrollTo({ top: 0 })
  }

  const advance = () => {
    trackOnboarding('onboarding_group_completed', { group: activeGroupKey })
    const nextKey = nextGroupKey(activeGroupKey, liveCtx())
    if (nextKey) goToGroup(nextKey)
  }

  const back = () => {
    const live = liveCtx()
    const prevKey = prevGroupKey(activeGroupKey, live)
    if (!prevKey) return
    const prevGroup = findGroup(prevKey)
    const count = prevGroup ? groupSteps(prevGroup, live).length : 0
    goToGroup(prevKey)
    // A group the user has already been through reopens fully answered rather
    // than collapsed back to its first question — they came back to change
    // something, so it all has to be on screen.
    dispatch({ type: SET_GROUP_REVEAL, payload: count + 1 })
  }

  /**
   * A question reporting that it has been answered. Reveal only ever grows:
   * revising an earlier answer must not re-hide the questions below it.
   */
  const revealAfter = (index: number) => {
    const current = (store.getState() as any).onboarding?.groupReveal || 1
    dispatch({ type: SET_GROUP_REVEAL, payload: Math.max(current, index + 2) })
  }

  const visible = steps.slice(0, Math.min(groupReveal, steps.length))
  // Reveal runs one past the last question when it is answered — that is what
  // brings up the group's Continue.
  //
  // Reveal only grows, so a placement test restarted after it had finished
  // (Back into the group, or a failed final submit) would otherwise leave
  // Continue on screen mid-retake, and taking it scored the learner from an
  // unfinished run.
  const placementPending = steps.includes('placement') && !placementDone
  const groupComplete = steps.length > 0 && groupReveal > steps.length && !placementPending

  // Bring a newly revealed question into view; the ones above it can be tall
  // enough to leave it entirely below the fold. Aligned to the top rather than
  // centred so a tall question (the reading test) starts at its beginning.
  useEffect(() => {
    if (groupReveal <= 1) return
    lastRevealedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [groupReveal])

  const progress = groupProgress(activeGroupKey, ctx)
  const canGoBack = Boolean(prevGroupKey(activeGroupKey, ctx))

  const chrome = (
    <header className='ob-topbar'>
      {canGoBack ? (
        <button type='button' className='ob-topbar-back' onClick={back} aria-label={t('nav.backLabel')}>
          <svg width='16' height='16' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
            <path
              d='M16 10H4M4 10L9 5M4 10L9 15'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
          {t('nav.back')}
        </button>
      ) : (
        <img className='ob-topbar-logo' src='/images/maayot-green-logo-small.svg' alt={BRAND_NAME} />
      )}
      {progress ? (
        <div
          className='ob-progress'
          role='progressbar'
          aria-valuemin={1}
          aria-valuemax={progress.total}
          aria-valuenow={progress.index}
        >
          <div className='ob-progress-track'>
            <div className='ob-progress-fill' style={{ width: `${(progress.index / progress.total) * 100}%` }} />
          </div>
          <span className='ob-progress-label'>{t('nav.progress', { index: progress.index, total: progress.total })}</span>
        </div>
      ) : canGoBack ? (
        <img
          className='ob-topbar-logo'
          src='/images/maayot-green-logo-small.svg'
          alt={BRAND_NAME}
          style={{ marginLeft: 'auto' }}
        />
      ) : null}
    </header>
  )

  // Pass-through group: result and trial own their whole screen.
  if (activeGroup && !activeGroup.header) {
    const PassThrough = STEP_COMPONENTS[steps[0]]
    return (
      <div className='ob-flow'>
        {chrome}
        {PassThrough ? <PassThrough next={advance} /> : null}
      </div>
    )
  }

  return (
    <div className='ob-flow'>
      {chrome}
      <div className='ob-group'>
        <h1 className='ob-group-header'>{activeGroup?.header ? t(activeGroup.header) : null}</h1>
        {activeGroup?.subheader && <p className='ob-group-subheader'>{t(activeGroup.subheader)}</p>}
        {visible.map((key, i) => {
          const Step = STEP_COMPONENTS[key]
          if (!Step) return null
          const isLastRevealed = i === visible.length - 1
          return (
            <div className='ob-group-q' key={key} ref={isLastRevealed ? lastRevealedRef : undefined}>
              <Step next={() => revealAfter(i)} />
            </div>
          )
        })}

        {groupComplete && (
          <div className='ob-group-actions'>
            <button type='button' className='ob-cta ob-cta--block' onClick={advance}>
              {t('nav.continue')}
              <svg width='18' height='18' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
                <path
                  d='M4 10H16M16 10L11 5M16 10L11 15'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default OnBoarding
